import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	flexRender,
	SortingState,
	ColumnFiltersState,
	VisibilityState,
	ColumnSizingState,
	ColumnOrderState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useTableConfiguration } from "../../helpers/useTableConfiguration";

interface TanStackEmployeeTableProps {
	tableId: string;
	data: any[];
	columns: any[];
	loading?: boolean;
	onRowClick?: (row: any, event: any) => void;
	onActionClick?: (row: any, event: any) => void;
	initialColumnVisibility?: Record<string, boolean>;
	columnVisibility?: Record<string, boolean>;
	onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
	pageSize?: number;
	height?: number | string;
	hasPermission?: boolean;
	enableRowResizing?: boolean;
	defaultRowHeight?: number;
	conditionalRowAction?: (row: any) => boolean;
	pageCount?: number;
	apiRef?: any;
	rowHeight?: number;
	onRowHeightChange?: (height: number) => void;
	meta?: any;
}

const TanStackEmployeeTable = forwardRef(
	(
		{
			tableId,
			data,
			columns,
			loading = false,
			onRowClick,
			onActionClick,
			initialColumnVisibility = {},
			columnVisibility: externalColumnVisibility,
			onColumnVisibilityChange,
			pageSize = 10,
			height = "530px",
			hasPermission = true,
			enableRowResizing = true,
			defaultRowHeight = 65,
			conditionalRowAction,
			pageCount,
			apiRef,
			rowHeight: externalRowHeight,
			onRowHeightChange,
			meta,
		}: TanStackEmployeeTableProps,
		ref,
	) => {
		const [sorting, setSorting] = useState<SortingState>([]);
		const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

		// ✅ FIX: Initialize these states, will be updated after config loads
		const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
			initialColumnVisibility,
		);
		const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
		const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);

		const [rowSelection, setRowSelection] = useState({});
		const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
		const [resizingRow, setResizingRow] = useState<string | null>(null);

		const [internalColumnVisibility, setInternalColumnVisibility] =
			useState<VisibilityState>(initialColumnVisibility);

		const getCurrentPageFromURL = () => {
			if (typeof window === "undefined") return 0;
			const searchParams = new URLSearchParams(window.location.search);
			const page = searchParams.get("page");
			return page ? parseInt(page) - 1 : 0;
		};

		const [pagination, setPagination] = useState({
			pageIndex: getCurrentPageFromURL(),
			pageSize: pageSize,
		});

		useEffect(() => {
			const currentPage = getCurrentPageFromURL();
			setPagination((prev) => ({ ...prev, pageIndex: currentPage }));
		}, [typeof window !== "undefined" ? window.location.search : ""]);

		// ✅ FIX: Create table with state handlers
		const table = useReactTable({
			data,
			columns,
			state: {
				sorting,
				columnFilters,
				columnVisibility,
				columnSizing,
				columnOrder,
				rowSelection,
				pagination,
			},

			onSortingChange: setSorting,
			onColumnFiltersChange: setColumnFilters,
			onColumnVisibilityChange: setColumnVisibility,
			onColumnSizingChange: setColumnSizing, // ✅ ADD THIS
			onColumnOrderChange: setColumnOrder, // ✅ ADD THIS
			onRowSelectionChange: setRowSelection,
			onPaginationChange: setPagination,
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: getSortedRowModel(),
			getFilteredRowModel: getFilteredRowModel(),
			meta: {
				...meta,
				onActionClick,
			},

			...(pageCount !== undefined
				? {
						manualPagination: true,
						pageCount: pageCount,
					}
				: {
						getPaginationRowModel: getPaginationRowModel(),
					}),

			enableColumnResizing: true,
			columnResizeMode: "onChange",
			columnResizeDirection: "ltr",

			defaultColumn: {
				minSize: 0,
				size: 200,
				maxSize: 800,
			},
		});

		// ✅ CRITICAL FIX: Use the hook and get rowHeight from it
		const tableConfig = useTableConfiguration(tableId, table, defaultRowHeight);

		useEffect(() => {
			const loadConfig = async () => {
				if (!table) return;

				const config = await tableConfig.loadAllConfigurations();

				if (config && config[tableId]) {
					const savedConfig = config[tableId];

					// Apply saved configuration to state
					if (
						savedConfig.columnWidths &&
						Object.keys(savedConfig.columnWidths).length > 0
					) {
						setColumnSizing(savedConfig.columnWidths);
					}

					if (savedConfig.columnVisibility) {
						setColumnVisibility(savedConfig.columnVisibility);
					}

					if (savedConfig.columnOrder && savedConfig.columnOrder.length > 0) {
						setColumnOrder(savedConfig.columnOrder);
					}
				}
			};

			loadConfig();
		}, []); // Run once on mount

		useImperativeHandle(ref, () => table);

		const handlePageChange = (newPageIndex: number) => {
			if (pageCount !== undefined) {
				const searchParams = new URLSearchParams(window.location.search);
				searchParams.set("page", String(newPageIndex + 1));
				const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
				window.history.pushState({}, "", newUrl);

				setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
				window.dispatchEvent(new PopStateEvent("popstate"));
			} else {
				setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
			}
		};

		const handleRowResize = (rowId: string, startY: number) => {
			if (!enableRowResizing) return;

			setResizingRow(rowId);
			// ✅ FIX: Use tableConfig.rowHeight instead of effectiveDefaultRowHeight
			const startHeight = rowHeights[rowId] || tableConfig.rowHeight;

			const handleMouseMove = (e: MouseEvent) => {
				const deltaY = e.clientY - startY;
				const newHeight = Math.max(40, Math.min(300, startHeight + deltaY));
				setRowHeights((prev) => ({ ...prev, [rowId]: newHeight }));
			};

			const handleMouseUp = () => {
				setResizingRow(null);
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		};

		useEffect(() => {
			if (externalColumnVisibility) {
				setInternalColumnVisibility(externalColumnVisibility);
			}
		}, [externalColumnVisibility]);

		const CustomNoRowsOverlay = () => {
			return (
				<div className="flex flex-col items-center justify-center h-full">
					<p className="text-sm text-black">
						You are not authorized to access this data.
					</p>
				</div>
			);
		};

		if (loading && hasPermission) {
			return (
				<div
					className="w-full flex items-center justify-center bg-white rounded-2xl"
					style={{ height }}
				>
					<div className="flex flex-col items-center gap-3">
						<div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
						<p className="text-sm text-gray-500">Loading...</p>
					</div>
				</div>
			);
		}

		return (
			<div
				className="w-full bg-white rounded-2xl overflow-hidden flex flex-col"
				style={{ height }}
			>
				<style>
					{`
          .table-scrollbar::-webkit-scrollbar {
            width: 16px;
            height: 16px;
          }
          .table-scrollbar::-webkit-scrollbar-track {
            background: #f7fafc;
          }
          .table-scrollbar::-webkit-scrollbar-thumb {
            background-color: #e2e8f0;
            border-radius: 8px;
            border: 3px solid #f7fafc;
          }
          .table-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: #cbd5e0;
            border-radius: 7px;
            border: 3px solid #f7fafc;
          }
        `}
				</style>

				<div className="flex-1 overflow-auto table-scrollbar">
					{!hasPermission ? (
						<CustomNoRowsOverlay />
					) : (
						<table
							className="w-full border-collapse"
							style={{ tableLayout: "fixed" }}
						>
							<thead
								className="sticky top-0 z-10"
								style={{ backgroundColor: "#F2F2F4" }}
							>
								{table.getHeaderGroups().map((headerGroup) => (
									<tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<th
												key={header.id}
												style={{
													width: `${header.getSize()}px`,
													position: "relative",
													backgroundColor: "#F2F2F4",
													height: "70px",
												}}
												className="text-left px-4 font-bold text-sm text-gray-800 border-b border-gray-200"
											>
												<div className="flex items-center gap-2 h-full">
													<div
														className={`flex-1 select-none truncate ${
															header.column.getCanSort()
																? "cursor-pointer hover:text-gray-900"
																: ""
														}`}
														onClick={header.column.getToggleSortingHandler()}
													>
														{header.isPlaceholder
															? null
															: flexRender(
																	header.column.columnDef.header,
																	header.getContext(),
																)}
													</div>
													{header.column.getCanSort() && (
														<button
															onClick={header.column.getToggleSortingHandler()}
															className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
														>
															{{
																asc: <ChevronUp className="w-4 h-4" />,
																desc: <ChevronDown className="w-4 h-4" />,
															}[header.column.getIsSorted() as string] ?? (
																<ChevronsUpDown className="w-4 h-4 text-gray-400" />
															)}
														</button>
													)}
												</div>

												<div
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
													className={`absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none ${
														header.column.getIsResizing()
															? "bg-blue-500"
															: "hover:bg-blue-400/50"
													}`}
												/>
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody>
								{table.getRowModel().rows.map((row, index) => {
									const rowId = row.id;
									// ✅ CRITICAL FIX: Use tableConfig.rowHeight instead of effectiveDefaultRowHeight
									const rowHeight = rowHeights[rowId] || tableConfig.rowHeight;
									const isRowClickable = conditionalRowAction
										? conditionalRowAction(row.original)
										: onRowClick !== undefined;

									return (
										<tr
											key={row.id}
											onClick={(e) => {
												if (isRowClickable && onRowClick) {
													onRowClick(row.original, e);
												}
											}}
											style={{
												height: `${rowHeight}px`,
												position: "relative",
											}}
											className={`
                      border-b border-gray-100 transition-colors truncate
                      ${
												index % 2 === 0
													? "bg-white even-row"
													: "bg-gray-50/50 odd-row"
											}
                      ${
												isRowClickable
													? "cursor-pointer hover:bg-blue-50/30"
													: ""
											}
                    `}
										>
											{row.getVisibleCells().map((cell) => (
												<td
													key={cell.id}
													className="px-4 text-sm text-gray-700"
													style={{
														width: `${cell.column.getSize()}px`,
														maxWidth: `${cell.column.getSize()}px`,
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</td>
											))}

											{enableRowResizing && (
												<div
													className="absolute bottom-0 left-0 right-0 h-1 cursor-row-resize select-none z-20"
													onMouseDown={(e) => {
														e.preventDefault();
														e.stopPropagation();
														handleRowResize(rowId, e.clientY);
													}}
												/>
											)}
										</tr>
									);
								})}
								{table.getRowModel().rows.length === 0 && (
									<tr>
										<td
											colSpan={columns.length}
											className="text-center py-12 text-gray-500 text-sm"
										>
											No data available
										</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>

				{hasPermission && (
					<div className="border-t border-gray-200 px-4 py-2 bg-gray-50 flex items-center justify-between">
						<div className="flex items-center gap-3">
							<span className="text-xs text-gray-600 font-medium">
								Row Height:
							</span>
							<input
								type="range"
								min="40"
								max="120"
								step="5"
								value={tableConfig.rowHeight}
								onChange={(e) =>
									tableConfig.setRowHeight(Number(e.target.value))
								}
								className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
							/>
							<span className="text-xs text-gray-600 font-mono min-w-[45px]">
								{tableConfig.rowHeight}px
							</span>
						</div>

						<button
							onClick={tableConfig.resetConfiguration}
							className="text-xs text-blue-600 hover:text-blue-800 font-medium"
						>
							Reset Table
						</button>
					</div>
				)}

				{hasPermission && (
					<div className="border-t border-gray-200 px-4 py-3 bg-white flex items-center justify-end flex-shrink-0">
						<div className="flex items-center gap-1">
							<span className="text-sm text-gray-600 mr-4">
								Page {pagination.pageIndex + 1} of {table.getPageCount()}
							</span>
							<button
								onClick={() => handlePageChange(0)}
								disabled={!table.getCanPreviousPage()}
								className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
								title="First page"
							>
								««
							</button>
							<button
								onClick={() => handlePageChange(pagination.pageIndex - 1)}
								disabled={!table.getCanPreviousPage()}
								className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
								title="Previous page"
							>
								‹
							</button>
							<button
								onClick={() => handlePageChange(pagination.pageIndex + 1)}
								disabled={!table.getCanNextPage()}
								className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
								title="Next page"
							>
								›
							</button>
							<button
								onClick={() => handlePageChange(table.getPageCount() - 1)}
								disabled={!table.getCanNextPage()}
								className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
								title="Last page"
							>
								»»
							</button>
						</div>
					</div>
				)}
			</div>
		);
	},
);

TanStackEmployeeTable.displayName = "TanStackEmployeeTable";

export default TanStackEmployeeTable;
